import { contentCopy } from '@/copy'
import { Avatar } from '../Avatar/Avatar'
import { cx } from '../cx'
import styles from './AvatarGroup.module.css'

export type Participant = {
  name: string
  kind: 'human' | 'agent'
  alt?: boolean
}

export function AvatarGroup({
  participants,
  max = 4,
  size = 'md',
  label,
  ground = 'page',
  className,
}: {
  participants: readonly Participant[]
  max?: number
  size?: 'sm' | 'md'
  label?: string
  ground?: 'page' | 'surface'
  className?: string
}) {
  const visible = participants.slice(0, max)
  const overflow = participants.length - visible.length
  // A name may repeat, so key on the name plus its occurrence count.
  const seen = new Map<string, number>()
  const keyed: { p: Participant; key: string; alt: boolean }[] = []
  for (const p of visible) {
    const n = (seen.get(p.name) ?? 0) + 1
    seen.set(p.name, n)
    // Avatar card: alt tells two adjacent people apart; the group is where
    // adjacency happens (D91).
    const before = keyed[keyed.length - 1]
    const alt =
      p.alt ?? (p.kind === 'human' && before?.p.kind === 'human' && !before.alt)
    keyed.push({ p, key: `${p.name}:${n}`, alt })
  }
  return (
    <span
      role="img"
      aria-label={
        label ??
        contentCopy.avatarGroup.label(
          visible.map((p) => p.name),
          overflow,
        )
      }
      className={cx(styles.group, className)}
      data-size={size}
      data-ground={ground}
    >
      <span className={styles.discs}>
        {keyed.map(({ p, key, alt }) => (
          <Avatar key={key} name={p.name} kind={p.kind} size={size} alt={alt} />
        ))}
      </span>
      {overflow > 0 && (
        <span className={styles.more} aria-hidden="true">
          +{overflow}
        </span>
      )}
    </span>
  )
}
